// O modelo AUTORAL do sudoku — a fonte da verdade do editor.
//
// É plano, imutável e serializável (contrato do shell: history, dirty tracking e
// undo saem disso de graça). Nada de `x`, `y`, string ou `hint` aqui dentro: toda a
// esquisitice do blob morre no `wire.ts` (D6).
//
// A grade tem a FORMA do doc (`shape`: 9×9 ou 15×15). Os índices são row-major e
// 0-based; o 1-based existe só na borda do blob.

import { SUDOKU_9, type SudokuShape } from '../../../../../games/Sudoku/shape';
import type { NewPuzzleParams } from '../../types';
import type { SudokuDificuldade } from '../../../../../services/admin';
import { CELLS, SIZE, generateGrid } from './solver';

export type { SudokuDificuldade, SudokuShape };
export { CELLS, SIZE };

export interface SudokuDoc {
  titulo: string;
  /** Forma da grade (lado e bloco). Vem do blob; documento novo é 9×9. */
  shape: SudokuShape;
  /** `size*size` valores 1..size, row-major. A solução completa: o jogador confere contra ela. */
  solution: number[];
  /** `size*size` flags: a casa já vem preenchida para o jogador? */
  givens: boolean[];
  /**
   * Dificuldade da ÚLTIMA geração — metadado, não regra. O admin pode gerar em
   * "fácil" e depois desligar casas até o tabuleiro ficar difícil; o campo só serve
   * para o botão "Gerar" lembrar a escolha anterior.
   */
  dificuldade: SudokuDificuldade;
}

/** Índice row-major da casa (linha, coluna), ambas 0-based. */
export function cellIndex(row: number, col: number, size = SIZE): number {
  return row * size + col;
}

/** Linha (0-based) da casa. */
export function rowOf(index: number, size = SIZE): number {
  return Math.floor(index / size);
}

/** Coluna (0-based) da casa. */
export function colOf(index: number, size = SIZE): number {
  return index % size;
}

/** Quantas casas vêm preenchidas. */
export function countGivens(doc: SudokuDoc): number {
  return doc.givens.reduce((total, given) => (given ? total + 1 : total), 0);
}

/**
 * O tabuleiro como o jogador o recebe: dígito nas casas dadas, `0` nas vazias.
 * É esta grade — não a solução — que vai ao solver.
 */
export function toPuzzle(doc: SudokuDoc): number[] {
  return doc.solution.map((digit, i) => (doc.givens[i] ? digit : 0));
}

/**
 * Documento novo — o par de `load()` no fluxo de criação.
 *
 * `params.largura`/`params.altura` são IGNORADOS de propósito: o tamanho do sudoku
 * é do tipo, não do autor — documento novo é sempre 9×9 (o 15×15 só chega por blob), o
 * módulo declara `dimensions: { mode: 'fixed' }` e o modal mostra 9 × 9 travado.
 *
 * Já nasce com um tabuleiro sorteado, e não vazio, porque sudoku é matematicamente
 * restrito (D1): gerar é o que garante uma solução válida de saída, e o ajuste do
 * admin é decidir QUAIS casas vêm preenchidas. Documento vazio só produziria uma
 * tela de erros até o primeiro "Gerar".
 */
export function createEmpty(params: NewPuzzleParams): SudokuDoc {
  const dificuldade: SudokuDificuldade = 'medio';
  const { solution, givens } = generateGrid(dificuldade);
  return { titulo: params.titulo, shape: SUDOKU_9, solution, givens, dificuldade };
}

/** Documento sem tabuleiro — usado pelo `wire` quando o blob vem ilegível. */
export function emptyDoc(titulo: string, shape: SudokuShape = SUDOKU_9): SudokuDoc {
  const cells = shape.size * shape.size;
  return {
    titulo,
    shape,
    solution: new Array(cells).fill(0),
    givens: new Array(cells).fill(false),
    dificuldade: 'medio',
  };
}
